import { useEffect, useState } from 'react';
import { getAdminLogs, getSecurityLogs } from '../../services/admin';
import styles from './Admin.module.css';

export function AdminLogs() {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'admin' | 'security'>('all');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    const [adminLogs, securityLogs] = await Promise.all([
      getAdminLogs(50),
      getSecurityLogs(50)
    ]);
    
    const combined = [
      ...adminLogs.map((l: any) => ({ ...l, type: 'ADMIN' })),
      ...securityLogs.map((l: any) => ({ ...l, type: 'SECURITY', action: l.event }))
    ].sort((a: any, b: any) => {
      const timeA = a.timestamp?.seconds || 0;
      const timeB = b.timestamp?.seconds || 0;
      return timeB - timeA;
    });

    setLogs(combined);
    setLoading(false);
  };

  const filteredLogs = logs.filter(log => {
    if (filter === 'all') return true;
    return log.type.toLowerCase() === filter.toLowerCase();
  });

  return (
    <div className={styles.adminPage}>
      <header className={styles.pageHeader}>
        <div>
          <h1>Audit & Security Logs</h1>
          <p>Complete administrative activity and security event trail.</p>
        </div>
        <div className={styles.headerActions}>
          <select 
            className={styles.actionSelect} 
            value={filter} 
            onChange={(e) => setFilter(e.target.value as any)}
          >
            <option value="all">All Logs</option>
            <option value="admin">Admin Actions</option>
            <option value="security">Security Events</option>
          </select>
        </div>
      </header>

      <div className={styles.adminCard}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>Loading logs...</div>
        ) : filteredLogs.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <h2 style={{ marginBottom: '8px' }}>No Logs Found</h2>
            <p style={{ color: 'var(--text-3)' }}>No logs match your current filter.</p>
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.adminTable}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Action/Event</th>
                  <th>Performed By / Target</th>
                  <th>Details</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <span className={styles.statusBadge} style={{ 
                        background: log.type === 'SECURITY' ? 'rgba(255, 68, 68, 0.1)' : 'rgba(57, 255, 20, 0.1)',
                        color: log.type === 'SECURITY' ? '#ff4444' : 'var(--accent)'
                      }}>
                        {log.type}
                      </span>
                    </td>
                    <td>{log.action}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{log.adminEmail || 'System'}</div>
                      {log.targetEmail && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>
                          Target: {log.targetEmail}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: '0.8rem' }}>
                        {log.prevValue !== undefined && (
                          <span style={{ color: '#ff4444' }}>{JSON.stringify(log.prevValue)} → </span>
                        )}
                        {log.newValue !== undefined && (
                          <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{JSON.stringify(log.newValue)}</span>
                        )}
                        {!log.prevValue && !log.newValue && JSON.stringify(log.details || {})}
                      </div>
                    </td>
                    <td>{log.timestamp?.toDate ? log.timestamp.toDate().toLocaleString() : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
